import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket, useSocketEvent } from './useSocket';

/** فرصت اتصال اولیه سوکت قبل از شروع polling پشتیبان */
const FALLBACK_DELAY_MS = 5000;
const SUBSCRIBE_TIMEOUT_MS = 10000;

const isSuccess = (res) => !res?.status || res.status === 'success';

/**
 * فید زنده اعلان از karmadws (مثل یادآور پیگیری و پیامک خوانده‌نشده).
 *
 * با هر اتصال سوکت `subscribe-<feed>` ارسال می‌شود و لیست فعلی در ack برمی‌گردد؛
 * بعد از آن سرور هر تغییر لیست را با رویداد `<feed>` push می‌کند.
 * فقط وقتی اشتراک زنده برقرار نیست (سوکت قطع یا subscribe ناموفق)، `fetchList` هر
 * `pollIntervalMs` صدا زده می‌شود تا قابلیت بدون سوکت هم کار کند.
 *
 * پاسخ سوکت و API یک شکل دارند: { status, count, notifications }.
 *
 * @param {Object} options
 * @param {string} options.subscribeEvent رویداد subscribe (client → server)
 * @param {string} options.event رویداد push (server → client)
 * @param {() => Promise<any>} options.fetchList فراخوانی API پشتیبان
 * @param {(res: any) => void} options.onResponse دریافت لیست کامل (از سوکت یا API)
 * @param {number|null} options.pollIntervalMs فاصله polling پشتیبان (null: فقط یک بار دریافت)
 * @param {boolean} options.enabled فعال بودن (کاربر لاگین است)
 * @param {string} [options.label] برچسب لاگ
 */
export const useLiveNotificationFeed = ({
  subscribeEvent,
  event,
  fetchList,
  onResponse,
  pollIntervalMs,
  enabled,
  label = event,
}) => {
  const { socket, isConnected, socketId } = useSocket();
  const [subscribed, setSubscribed] = useState(false);
  const [subscribeFailed, setSubscribeFailed] = useState(false);
  const fetchingRef = useRef(false);
  const onResponseRef = useRef(onResponse);
  const fetchListRef = useRef(fetchList);

  useEffect(() => {
    onResponseRef.current = onResponse;
    fetchListRef.current = fetchList;
  });

  const isLive = enabled && Boolean(socket) && isConnected;

  const handleResponse = useCallback((res) => {
    if (isSuccess(res)) onResponseRef.current?.(res);
  }, []);

  useSocketEvent(event, handleResponse, enabled);

  const fetchOnce = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      handleResponse(await fetchListRef.current());
    } catch (error) {
      console.warn(`[${label}] fetch failed:`, error?.message || error);
    } finally {
      fetchingRef.current = false;
    }
  }, [handleResponse, label]);

  // هر اتصال جدید سوکت (شامل reconnect) دوباره subscribe می‌کند و وضعیت فعلی را می‌گیرد.
  // unsubscribe لازم نیست: با خروج کاربر سوکت قطع می‌شود و سرور اشتراک را حذف می‌کند.
  useEffect(() => {
    setSubscribed(false);
    setSubscribeFailed(false);
    if (!isLive) return undefined;

    let cancelled = false;
    socket.timeout(SUBSCRIBE_TIMEOUT_MS).emit(subscribeEvent, (err, res) => {
      if (cancelled) return;
      if (!err && isSuccess(res)) {
        handleResponse(res);
        setSubscribed(true);
        return;
      }
      console.warn(`[${label}] subscribe failed, falling back to API polling:`, err || res);
      setSubscribeFailed(true);
    });

    return () => {
      cancelled = true;
    };
  }, [isLive, socket, socketId, subscribeEvent, handleResponse, label]);

  // polling پشتیبان فقط وقتی اشتراک زنده برقرار نیست؛
  // اگر subscribe صریحاً شکست خورد، بدون انتظار از API گرفته می‌شود
  useEffect(() => {
    if (!enabled || (isLive && subscribed)) return undefined;

    let interval = null;
    const start = setTimeout(() => {
      fetchOnce();
      if (pollIntervalMs) interval = setInterval(fetchOnce, pollIntervalMs);
    }, subscribeFailed ? 0 : FALLBACK_DELAY_MS);

    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [enabled, isLive, subscribed, subscribeFailed, fetchOnce, pollIntervalMs]);

  return { isLive: isLive && subscribed };
};

export default useLiveNotificationFeed;
